import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import dayjs from 'dayjs';
import { CalendarRange } from 'lucide-react';

import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

function addToDate(date, amount, type) {
  const result = new Date(date);

  if (type === 'days') {
    result.setDate(result.getDate() + amount);
  } else {
    // default to months
    result.setMonth(result.getMonth() + amount);
  }

  return result;
}

function DateRangeFilter({
  value,
  onChange,
  limit, // number
  limitType = 'months', // "months" | "days"
  onCalendarOpen: externalOnCalendarOpen,
  onCalendarClose: externalOnCalendarClose,
  ...props
}) {
  const pickerValue = useMemo(() => {
    if (!Array.isArray(value)) {
      return [null, null];
    }

    return value.map((item) => {
      if (!item) {
        return null;
      }

      if (item instanceof Date) {
        return item;
      }

      const parsedDate = new Date(item);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    });
  }, [value?.[0], value?.[1]]);

  const externalValueKey = useMemo(() => {
    const startTime = pickerValue?.[0] ? pickerValue[0].getTime() : 'null';
    const endTime = pickerValue?.[1] ? pickerValue[1].getTime() : 'null';
    return `${startTime}_${endTime}`;
  }, [pickerValue]);

  const [internalValue, setInternalValue] = useState(pickerValue);
  const [isDraftRange, setIsDraftRange] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const pendingRangeRef = useRef(null);

  const clampAndCommitRange = useCallback(
    (range) => {
      let [start, end] = range || [];

      if (!start || !end) {
        return false;
      }

      if (end < start) {
        [start, end] = [end, start];
      }

      if (limit && limit > 0) {
        const maxEndDate = addToDate(start, limit, limitType);
        if (end > maxEndDate) {
          end = maxEndDate;
        }
      }

      const nextRange = [start, end];
      setInternalValue(nextRange);
      pendingRangeRef.current = nextRange;
      onChange([
        dayjs(start).format('YYYY-MM-DD'),
        dayjs(end).format('YYYY-MM-DD'),
      ]);

      return true;
    },
    [limit, limitType, onChange],
  );

  const isSameRange = useCallback((left, right) => {
    const [leftStart, leftEnd] = left || [];
    const [rightStart, rightEnd] = right || [];

    const leftStartTime =
      leftStart instanceof Date ? leftStart.getTime() : null;
    const leftEndTime = leftEnd instanceof Date ? leftEnd.getTime() : null;
    const rightStartTime =
      rightStart instanceof Date ? rightStart.getTime() : null;
    const rightEndTime = rightEnd instanceof Date ? rightEnd.getTime() : null;

    return leftStartTime === rightStartTime && leftEndTime === rightEndTime;
  }, []);

  useEffect(() => {
    setInternalValue(pickerValue);
    pendingRangeRef.current = null;
    setIsDraftRange(false);
  }, [externalValueKey]);

  const maxDate = useMemo(() => {
    if (isDraftRange) {
      return undefined;
    }

    const today = new Date();

    if (!limit || limit <= 0) {
      return today;
    }

    const startDate = pickerValue?.[0];
    if (!startDate) {
      return today;
    }

    const maxRangeDate = addToDate(startDate, limit, limitType);
    return maxRangeDate > today ? today : maxRangeDate;
  }, [isDraftRange, pickerValue, limit, limitType]);

  const defaultActiveStartDate = useMemo(
    () => pickerValue?.[0] || pickerValue?.[1] || new Date(),
    [externalValueKey],
  );

  const mergedCalendarProps = useMemo(
    () => ({
      ...props.calendarProps,
      key: `${externalValueKey}_${isCalendarOpen ? 'open' : 'closed'}`,
      defaultActiveStartDate,
    }),
    [
      defaultActiveStartDate,
      externalValueKey,
      isCalendarOpen,
      props.calendarProps,
    ],
  );

  const handleCalendarOpen = useCallback(() => {
    setIsCalendarOpen(true);
    if (externalOnCalendarOpen) {
      externalOnCalendarOpen();
    }
  }, [externalOnCalendarOpen]);

  const handleChange = useCallback(
    (nextValue) => {
      setInternalValue(nextValue || [null, null]);
      setIsDraftRange(Boolean(nextValue));

      let [start, end] = nextValue || [];

      if (!start && !end) {
        pendingRangeRef.current = null;
        onChange([null, null]);
        return;
      }

      // Keep typed or in-progress values local until the user finishes the range.
      if (!start || !end) {
        pendingRangeRef.current = [start || null, end || null];
        return;
      }

      // Normalize order
      if (end < start) {
        [start, end] = [end, start];
      }

      const committedRange = [start, end];
      pendingRangeRef.current = committedRange;
      setIsDraftRange(false);
      clampAndCommitRange(committedRange);
    },
    [clampAndCommitRange],
  );

  const handleCalendarClose = useCallback(() => {
    const pendingRange = pendingRangeRef.current || internalValue;
    const shouldSkipCommit =
      pendingRange && pickerValue && isSameRange(pendingRange, pickerValue);
    const didCommit = shouldSkipCommit
      ? true
      : clampAndCommitRange(pendingRange);
    setIsDraftRange(false);
    setIsCalendarOpen(false);

    if (!didCommit) {
      pendingRangeRef.current = null;
      setInternalValue(pickerValue);
      if (externalOnCalendarClose) {
        externalOnCalendarClose();
      }
      return;
    }

    if (externalOnCalendarClose) {
      externalOnCalendarClose();
    }
  }, [
    clampAndCommitRange,
    externalOnCalendarClose,
    internalValue,
    isSameRange,
    pickerValue,
  ]);

  return (
    <DateRangePicker
      {...props}
      value={internalValue}
      onChange={handleChange}
      onCalendarOpen={handleCalendarOpen}
      onCalendarClose={handleCalendarClose}
      calendarProps={mergedCalendarProps}
      maxDate={maxDate}
      rangeDivider=""
      format="dd-MM-y"
      clearIcon={null}
      calendarIcon={<CalendarRange size={18} color="white" />}
    />
  );
}

export { DateRangeFilter };
